import { BRAND_OWNER } from './brands';
import { eokManWon } from './money';
/**
 * cl-core/fact-check — 저장 생성문(analysis_text·description·faq·curated_copy) vs 구조화 필드 대조(acc-r · 2026-10-16).
 *
 * 근거: report/acc/audit_20261016 · `_acc_text_20261016`(293행/264곳) —
 *   분석글 「분양가 미공개」인데 price 있음 169 · 시공사 불일치 87 · 세대 20 · 입주 연도 17.
 *
 * 계약(지시 1a):
 *   · 문장 단위 판정. 모순 문장은 숨기고(hide) 구조화 필드로 짠 문장 1개로 대체(replace — 같은 종류는 글 하나에 1회).
 *   · rank_lock 보존 문장(locked)은 숨기지 않는다 — «숫자만» 구조화 값으로 치환(substitute).
 *     숫자가 아닌 모순(시공사명·「미공개」)은 손대지 않고 그대로 둔다(CL12 보존율: 숫자 토큰만 다른 절 = 보존).
 *   · 대조할 구조화 값이 없으면 판정하지 않는다(모른다 ≠ 틀렸다).
 *   · 분양가는 «노출 가능한 값» 만 넘긴다(출처 정책 — 호출부가 price-guard·출처 판정 뒤의 값을 준다).
 *
 * ⛔ 외부 패키지 · Node API · '@/' 별칭 import 금지(cl-core 규칙).
 */

export type FactKind = 'price_undisclosed' | 'price_conflict' | 'builder_conflict' | 'units_conflict' | 'movein_year_conflict';
export type FactAction = 'keep' | 'hide' | 'replace' | 'substitute' | 'locked';

export interface FactFields {
  /** 단지 총세대(complex_units). 회차 공급 수(total_units 의 회차 기록)는 넣지 않는다. */
  complexUnits?: number | null;
  /** 이번 회차 공급(supply_units) */
  supplyUnits?: number | null;
  /** 일반공급(general_units) */
  generalUnits?: number | null;
  /** 시공사(쉼표·슬래시 구분 복수 가능). 깨진 값(「0」「1」「000」)은 무시된다. */
  builder?: string | null;
  /** 만원. 노출 가능한 값만. */
  priceMin?: number | null;
  priceMax?: number | null;
  /** 입주 예정 「YYYYMM」(또는 YYYY-MM·YYYY.MM) */
  moveInYm?: string | number | null;
  /** price_source — 'applyhome_notice' 면 대체 문장에 「입주자모집공고 기준」. */
  priceSource?: string | null;
}

export interface SentenceVerdict {
  /** 원문 문장(뒤 공백 제외) */
  text: string;
  kinds: FactKind[];
  action: FactAction;
  /** 표시 문자열(hide 면 null) */
  out: string | null;
}

export interface FactCheckStats {
  sentences: number;
  /** 모순 문장 수(판정 기준 — 처리 전) */
  contradictions: number;
  hidden: number;
  replaced: number;
  substituted: number;
  /** rank_lock 문장인데 숫자로 고칠 수 없어 그대로 둔 수 */
  locked: number;
}

export interface FactCheckResult {
  text: string;
  sentences: SentenceVerdict[];
  stats: FactCheckStats;
  changed: boolean;
}

export interface FactCheckOptions {
  /** rank_lock 보존 문장 판정. `lockMatcher(목록)` 으로 만든다. */
  locked?: (sentence: string) => boolean;
  /** false 면 숨기기만 하고 대체 문장을 넣지 않는다. 기본 true. */
  replace?: boolean;
}

/* ───────────────────────── 공용 ───────────────────────── */

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const squash = (s: string) => String(s ?? '').normalize('NFC').replace(/[\s ]+/g, '');

/** rank_lock 보존 문장 목록 → 판정 함수(공백 무시 · 문장이 보존 문장을 품거나 보존 문장에 들어 있으면 locked). */
export function lockMatcher(list: Iterable<string> | null | undefined): (sentence: string) => boolean {
  const keys = [...(list ?? [])].map(squash).filter((x) => x.length >= 6);
  if (!keys.length) return () => false;
  return (sentence: string) => {
    const s = squash(sentence);
    if (s.length < 6) return false;
    return keys.some((k) => k.includes(s) || s.includes(k));
  };
}

/** 정비 원장·현장 시공사 깨진 값(「0」「1」「000」「-」 등 숫자·구두점뿐) → true. 지시 7. */
export function isBrokenBuilderValue(v: unknown): boolean {
  const s = String(v ?? '').trim();
  return !s || /^[0-9\s.,\-_/\\|·?*#()]+$/.test(s);
}

/** 만원 → 「3억 6,472만원」/「8,500만원」/「12억」. */
export function fmtManwonText(v: number): string {
  return eokManWon(v) ?? `${(Math.round(v) % 10000).toLocaleString('ko-KR')}만원`; // 정본 ./money
}

export function priceRangeText(min: number | null | undefined, max: number | null | undefined): string | null {
  const a = num(min); const b = num(max);
  // 2026-10-24 Node 「분양가는 최저가로」 — 대체 문장도 «바닥부터»(「분양가는 … 6억 9,000만원부터입니다」).
  if (a && b && a !== b) return `${fmtManwonText(Math.min(a, b))}부터`;
  const one = a ?? b;
  return one ? fmtManwonText(one) : null;
}

function parseYm(v: unknown): { y: number; m: number | null } | null {
  const d = String(v ?? '').replace(/[^0-9]/g, '');
  if (d.length < 4) return null;
  const y = Number(d.slice(0, 4));
  if (!(y > 1990 && y < 2100)) return null;
  const m = d.length >= 6 ? Number(d.slice(4, 6)) : null;
  return { y, m: m && m >= 1 && m <= 12 ? m : null };
}

/* ───────────────────────── 시공사 ───────────────────────── */

/** 표기 정규화(법인 꼬리표·공백·「건설부문」 제거 · 소문자) + 별칭 → 정본 키. */
const BUILDER_ALIAS: Record<string, string> = {
  '대림산업': 'dl이앤씨', 'dle&c': 'dl이앤씨', 'dl이앤씨': 'dl이앤씨', 'dl건설': 'dl건설',
  '포스코건설': '포스코이앤씨', '포스코이앤씨': '포스코이앤씨', 'posco이앤씨': '포스코이앤씨',
  'sk건설': 'sk에코플랜트', 'sk에코플랜트': 'sk에코플랜트',
  '현대산업개발': 'hdc현대산업개발', 'hdc현대산업개발': 'hdc현대산업개발', 'hdc': 'hdc현대산업개발',
  '한화건설': '한화', '한화': '한화', '㈜한화': '한화',
  '삼성물산': '삼성물산', '삼성물산건설': '삼성물산',
  'gs건설': 'gs건설', '지에스건설': 'gs건설',
  '현대엔지니어링': '현대엔지니어링', '현대건설': '현대건설', '대우건설': '대우건설', '롯데건설': '롯데건설',
};

export function normalizeBuilderKey(v: string): string {
  const k = String(v ?? '')
    .normalize('NFC')
    .replace(/\(주\)|㈜|주식회사|\(유\)|유한회사/g, '')
    .replace(/건설부문|건설사업부문/g, '건설')
    .replace(/[\s·.]/g, '')
    .toLowerCase();
  if (BUILDER_ALIAS[k]) return BUILDER_ALIAS[k];
  // 원장 표기 흔들림(실측 「아이파크현대산업개발」「에이치디씨현대산업개발」) → 정본
  if (/현대산업개발$/.test(k)) return 'hdc현대산업개발';
  return k;
}

/** 본문에서 잡는 시공사 이름(1군·지방 주요 — 실측 표본에 나온 것 위주). 긴 이름 먼저. */
const KNOWN_BUILDERS = [
  'HDC현대산업개발', '현대산업개발', '현대엔지니어링', '현대건설', '삼성물산', 'GS건설', '대우건설', 'DL이앤씨', 'DL건설', '대림산업',
  '포스코이앤씨', '포스코건설', '롯데건설', 'SK에코플랜트', 'SK건설', '한화건설', '호반건설', '중흥건설', '중흥토건', '태영건설', '코오롱글로벌',
  '두산건설', '두산에너빌리티', '쌍용건설', '금호건설', '계룡건설', '한신공영', '동부건설', '반도건설', '우미건설', '제일건설', '서희건설',
  '대방건설', '동원개발', '효성중공업', '신세계건설', 'KCC건설', '아이에스동서', '라인건설', '양우건설', '대광건설', '우방산업', '우방', '한진중공업',
  '진흥기업', '금강주택', '대우산업개발', '삼환기업', '한양건설', '유림E&C',
];
// 조사(「대우건설의」)가 붙어도 잡는다 — 영문 이어짐만 막는다. 「한양」 단독은 브랜드(한양립스·한양수자인)와 겹쳐 목록에서 뺐다.
const BUILDER_RE = new RegExp(
  `(${[...KNOWN_BUILDERS].sort((a, b) => b.length - a.length).map((x) => x.replace(/[.*+?^${}()|[\]\\&]/g, '\\$&')).join('|')})(?![A-Za-z])`,
  'gi',
);

/* 브랜드 → 시공사 정본 키(BRAND_OWNER)는 ./brands 한 벌에서 유도한다(HC 정리 2026-10-24). 구조화 builder 칸에 브랜드가 든 행
 *   (실측 「푸르지오」「어울림」)을 시공사로 읽는다. */

function builderSet(raw: string | null | undefined): string[] {
  if (raw == null || isBrokenBuilderValue(raw)) return [];
  const keys = String(raw).split(/[,/·、]|\s외\s|\s및\s/).map((x) => x.trim()).filter((x) => x && !isBrokenBuilderValue(x)).map(normalizeBuilderKey);
  // 공백으로만 이어 적은 복수 시공사(실측 「삼성물산 대림산업 HDC현대산업개발」)는 본문 회사명 사전으로 한 번 더 뜯는다.
  const inner = builderMentions(String(raw)).map((m) => m.key);
  return [...new Set([...keys, ...inner].flatMap((k) => [k, ...(BRAND_OWNER[k] ?? [])]))];
}

function builderMentions(sentence: string): Array<{ raw: string; key: string; index: number }> {
  const out: Array<{ raw: string; key: string; index: number }> = [];
  const re = new RegExp(BUILDER_RE.source, BUILDER_RE.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(sentence))) out.push({ raw: m[1], key: normalizeBuilderKey(m[1]), index: m.index });
  return out;
}

const builderMatches = (key: string, set: readonly string[]) =>
  set.some((s) => s === key || (s.length >= 3 && key.length >= 3 && (s.includes(key) || key.includes(s))));

// 시공사를 «이 단지의» 시공사로 말하는 문맥. 비교·인근 단지 언급은 판정하지 않는다.
const BUILDER_CTX = /시공|브랜드|건설사|계열|대표|짓|맡|컨소시엄|주도/;
const OTHER_CTX = /인근|주변|다른\s*단지|경쟁\s*단지|비교|대비|과\s*같은|처럼|계열사|자회사|모회사|그룹사/;

const BRAND_RE = new RegExp(`(${Object.keys(BRAND_OWNER).sort((a, b) => b.length - a.length).join('|')})`, 'gi');

/** 「{브랜드}(브랜드)는 {다른 회사}의 …」 — 브랜드 뒤 25자 안의 회사가 브랜드 주인이 아니면 true. */
function brandMisattributed(sentence: string): boolean {
  if (!/브랜드|라인|계열/.test(sentence)) return false;
  const re = new RegExp(BRAND_RE.source, 'gi');
  let m: RegExpExecArray | null;
  const companies = builderMentions(sentence);
  while ((m = re.exec(sentence))) {
    const owners = BRAND_OWNER[m[1].toLowerCase()] ?? BRAND_OWNER[m[1]] ?? [];
    const end = m.index + m[1].length;
    const near = companies.filter((c) => c.index >= end && c.index - end <= 25);
    if (near.length && near.every((c) => !builderMatches(c.key, owners))) return true;
  }
  return false;
}

/* ───────────────────────── 판정 규칙 ───────────────────────── */

const PRICE_WORD = '(?:분양가(?:격)?|공급\\s*(?:가격|금액|가)|분양\\s*(?:가격|금액))';
const UNDISCLOSED = '(?:미공개|미정|비공개|공개되지\\s*않|발표되지\\s*않|확정되지\\s*않|알려지지\\s*않|나오지\\s*않|공개\\s*전|발표\\s*전|정해지지\\s*않)';
const PRICE_UNDISCLOSED_RE = new RegExp(`${PRICE_WORD}[^.!?\\n]{0,20}?${UNDISCLOSED}|${UNDISCLOSED}[^.!?\\n]{0,4}${PRICE_WORD}`);

// 금액 토큰: 「3.6억」「3억 6,472만원」「8,500만원」「36,000만 원」
const AMOUNT_RE = /(\d+(?:\.\d+)?)\s*억(?:\s*(\d{1,2},\d{3}|\d{1,4})\s*만)?\s*(?:원)?|(\d{1,3}(?:,\d{3})+|\d{3,6})\s*만\s*원/g;
const PRICE_OWN_CTX = new RegExp(PRICE_WORD);
const PRICE_OTHER_CTX = /평균|시세|인근|주변|매매|비교|대비|중고|기존|평당|㎡당|3\.3\s*㎡|평형당|전세|월세|대출|중도금|계약금|프리미엄|웃돈|수준으로|추정|예상|가능성|경향|형성/;

const UNITS_RES = [
  /총\s*(\d{1,3}(?:,\d{3})+|\d+)\s*(?:세대|가구)/g,
  /전체\s*(\d{1,3}(?:,\d{3})+|\d+)\s*(?:세대|가구)/g,
  /(\d{1,3}(?:,\d{3})+|\d+)\s*(?:세대|가구)\s*(?:규모|대단지)/g,
  // live1-d ④ — 「계획 2,466세대」(정비 원장 계획 세대 문장 · rank_lock 보존 문장 문현3)
  /계획\s*(\d{1,3}(?:,\d{3})+|\d+)\s*(?:세대|가구)/g,
];

const MOVEIN_RES = [
  // 「2025년 3월 입주」「2025년 입주」
  /(20\d{2})\s*년(?:\s*(\d{1,2})\s*월)?(?:\s*(?:중|말|초|경|께|예정|목표))?[^.!?\n\d]{0,6}입주/g,
  // 「입주 예정일은 202503」「입주 예정 2025.03」「입주는 2025년 3월」
  /입주[^.!?\n\d]{0,10}?(20\d{2})(?:\s*년(?:\s*(\d{1,2})\s*월)?|[.\-/](\d{1,2})|(\d{2})(?!\d))?/g,
];

const amountToManwon = (m: RegExpExecArray): number | null => {
  if (m[1] != null) {
    const eok = Number(m[1]);
    const man = m[2] ? Number(m[2].replace(/,/g, '')) : 0;
    return Number.isFinite(eok) ? eok * 10000 + man : null;
  }
  if (m[3] != null) return Number(m[3].replace(/,/g, ''));
  return null;
};

interface Hit { kind: FactKind; /** 숫자 치환(locked 용). 없으면 숫자로 못 고친다. */ sub?: (s: string) => string }

/** 문장 하나 → 모순 목록(구조화 값이 없는 종류는 판정하지 않는다). */
export function checkSentence(sentence: string, f: FactFields): FactKind[] {
  return hitsOf(sentence, f).map((h) => h.kind);
}

function hitsOf(sentence: string, f: FactFields): Hit[] {
  const s = String(sentence ?? '');
  const hits: Hit[] = [];
  const pMin = num(f.priceMin); const pMax = num(f.priceMax);
  const hasPrice = !!(pMin || pMax);

  // ① 분양가 미공개류 vs price 있음 — 숫자가 없어 숫자 치환 불가.
  if (hasPrice && PRICE_UNDISCLOSED_RE.test(s)) hits.push({ kind: 'price_undisclosed' });

  // ② 분양가 금액 vs 구조화(이 단지 분양가를 말하는 문장만 · 비교·추정 문맥 제외 · ±15%)
  if (hasPrice && PRICE_OWN_CTX.test(s) && !PRICE_OTHER_CTX.test(s) && !hits.length) {
    const lo = Math.min(pMin ?? pMax!, pMax ?? pMin!); const hi = Math.max(pMin ?? pMax!, pMax ?? pMin!);
    const re = new RegExp(AMOUNT_RE.source, 'g');
    let m: RegExpExecArray | null; let bad = false;
    while ((m = re.exec(s))) {
      const v = amountToManwon(m);
      if (v != null && v > 0 && (v < lo * 0.85 || v > hi * 1.15)) bad = true;
    }
    if (bad) {
      hits.push({
        kind: 'price_conflict',
        sub: (t) => {
          let i = 0;
          return t.replace(new RegExp(AMOUNT_RE.source, 'g'), (whole, ...g) => {
            const target = i++ === 0 ? lo : hi;
            return reshapeAmount(whole, g as string[], target);
          });
        },
      });
    }
  }

  // ③ 시공사
  const bset = builderSet(f.builder);
  if (bset.length && BUILDER_CTX.test(s) && !OTHER_CTX.test(s)) {
    const wrong = builderMentions(s).filter((x) => !builderMatches(x.key, bset));
    if (wrong.length) hits.push({ kind: 'builder_conflict' });
  }
  // ③-b 브랜드 소유 오기(「푸르지오는 현대건설의 … 브랜드」) — 구조화 값과 무관하게 틀린 문장. 비교 문맥이어도 잡는다.
  if (!hits.some((h) => h.kind === 'builder_conflict') && brandMisattributed(s)) hits.push({ kind: 'builder_conflict' });

  // ④ 세대(「총 N세대」 = complex_units 만)
  const cu = num(f.complexUnits);
  if (cu) {
    let bad = false;
    for (const re of UNITS_RES) {
      const r = new RegExp(re.source, 'g'); let m: RegExpExecArray | null;
      while ((m = r.exec(s))) {
        const v = Number(m[1].replace(/,/g, ''));
        if (v > 0 && Math.abs(v - cu) / cu > 0.02) bad = true;
      }
    }
    if (bad) {
      hits.push({
        kind: 'units_conflict',
        sub: (t) => {
          let out = t;
          for (const re of UNITS_RES) {
            out = out.replace(new RegExp(re.source, 'g'), (whole: string, n: string) => {
              const v = Number(String(n).replace(/,/g, ''));
              if (!(v > 0) || Math.abs(v - cu) / cu <= 0.02) return whole;
              return whole.replace(n, n.includes(',') || cu >= 1000 ? cu.toLocaleString('ko-KR') : String(cu));
            });
          }
          return out;
        },
      });
    }
  }

  // ⑤ 입주 연도
  const mv = parseYm(f.moveInYm);
  if (mv) {
    let bad = false;
    for (const re of MOVEIN_RES) {
      const r = new RegExp(re.source, 'g'); let m: RegExpExecArray | null;
      while ((m = r.exec(s))) if (Number(m[1]) !== mv.y) bad = true;
    }
    if (bad) {
      hits.push({
        kind: 'movein_year_conflict',
        sub: (t) => {
          let out = t;
          for (const re of MOVEIN_RES) {
            out = out.replace(new RegExp(re.source, 'g'), (whole: string, y: string, m1?: string, m2?: string, m3?: string) => {
              if (Number(y) === mv.y) return whole;
              let w = whole.replace(y, String(mv.y));
              const mo = m1 ?? m2 ?? m3;
              if (mo && mv.m) {
                const pad = mo.length === 2 ? String(mv.m).padStart(2, '0') : String(mv.m);
                const at = w.indexOf(String(mv.y)) + 4;
                w = w.slice(0, at) + w.slice(at).replace(mo, pad);
              }
              return w;
            });
          }
          return out;
        },
      });
    }
  }
  return hits;
}

/** 금액 토큰 모양을 유지한 채 숫자만 바꾼다(CL12 numericAsKept — 「N억 N만원」 모양 보존). */
function reshapeAmount(whole: string, g: string[], target: number): string {
  const n = Math.round(target);
  if (g[0] != null) {
    // 억 표기
    if (g[1] != null) {
      const eok = Math.floor(n / 10000); const man = n % 10000;
      return whole.replace(g[0], String(eok)).replace(g[1], g[1].includes(',') ? man.toLocaleString('ko-KR') : String(man));
    }
    const dec = g[0].includes('.') ? (g[0].split('.')[1] || '').length : 0;
    const v = n / 10000;
    return whole.replace(g[0], dec ? v.toFixed(dec) : String(Math.round(v)));
  }
  if (g[2] != null) return whole.replace(g[2], g[2].includes(',') ? n.toLocaleString('ko-KR') : String(n));
  return whole;
}

/** 구조화 필드로 짠 대체 문장(값이 없으면 null). */
export function replacementSentence(kind: FactKind, f: FactFields): string | null {
  switch (kind) {
    case 'price_undisclosed':
    case 'price_conflict': {
      const r = priceRangeText(f.priceMin, f.priceMax);
      return r ? `분양가는 ${f.priceSource === 'applyhome_notice' ? '입주자모집공고 기준 ' : ''}${r}입니다.` : null;
    }
    case 'builder_conflict': {
      const b = String(f.builder ?? '').split(/[,/]/).map((x) => x.trim()).filter((x) => x && !isBrokenBuilderValue(x));
      return b.length ? `시공사는 ${b.join('·')}입니다.` : null;
    }
    case 'units_conflict': {
      const cu = num(f.complexUnits);
      return cu ? `단지 전체는 총 ${cu.toLocaleString('ko-KR')}세대입니다.` : null;
    }
    case 'movein_year_conflict': {
      const mv = parseYm(f.moveInYm);
      return mv ? `입주 예정 시기는 ${mv.y}년${mv.m ? ` ${mv.m}월` : ''}입니다.` : null;
    }
  }
  return null;
}

/* ───────────────────────── 문장 분할·전체 판정 ───────────────────────── */

/** 문장 분할(문장부호·줄바꿈 기준). 각 조각은 [본문, 뒤 공백/구분자]. */
export function splitSentences(text: string): Array<[string, string]> {
  const s = String(text ?? '');
  const out: Array<[string, string]> = [];
  const isEnd = (i: number) => {
    const c = s[i];
    if (c === '\n') return true;
    if (!/[.!?。]/.test(c)) return false;
    // 「3.3㎡」「2.5억」 소수점은 문장 끝이 아니다
    if (c === '.' && /\d/.test(s[i - 1] ?? '') && /\d/.test(s[i + 1] ?? '')) return false;
    return true;
  };
  let i = 0;
  while (i < s.length) {
    let j = i;
    while (j < s.length && !isEnd(j)) j++;
    if (j < s.length && s[j] !== '\n') { j++; while (j < s.length && /[.!?。"'”’)\]]/.test(s[j])) j++; }
    const body = s.slice(i, j);
    let k = j;
    while (k < s.length && /\s/.test(s[k])) k++;
    out.push([body, s.slice(j, k)]);
    i = k > i ? k : i + 1;
  }
  return out;
}

const emptyStats = (): FactCheckStats => ({ sentences: 0, contradictions: 0, hidden: 0, replaced: 0, substituted: 0, locked: 0 });

/**
 * 글 하나 판정·수리. 반환 `text` 가 표시용(숨긴 문장 제거 · 대체 문장 삽입 · locked 숫자 치환).
 */
export function factCheckText(text: string | null | undefined, f: FactFields, opts: FactCheckOptions = {}): FactCheckResult {
  const src = String(text ?? '');
  const stats = emptyStats();
  const sentences: SentenceVerdict[] = [];
  if (!src.trim()) return { text: src, sentences, stats, changed: false };
  const usedReplacement = new Set<string>();
  const doReplace = opts.replace !== false;
  let out = '';
  for (const [body, gap] of splitSentences(src)) {
    if (!body.trim()) { out += body + gap; continue; }
    stats.sentences++;
    const hits = hitsOf(body, f);
    if (!hits.length) { sentences.push({ text: body, kinds: [], action: 'keep', out: body }); out += body + gap; continue; }
    stats.contradictions++;
    const kinds = hits.map((h) => h.kind);
    if (opts.locked?.(body)) {
      const subs = hits.filter((h) => h.sub);
      if (subs.length === hits.length) {
        const t = subs.reduce((acc, h) => h.sub!(acc), body);
        stats.substituted++;
        sentences.push({ text: body, kinds, action: 'substitute', out: t });
        out += t + gap;
      } else {
        stats.locked++;
        sentences.push({ text: body, kinds, action: 'locked', out: body });
        out += body + gap;
      }
      continue;
    }
    // 숨김 + (종류별 1회) 대체
    let rep: string | null = null;
    if (doReplace) {
      for (const k of kinds) {
        const r = replacementSentence(k, f);
        if (r && !usedReplacement.has(r)) { usedReplacement.add(r); rep = rep ? `${rep} ${r}` : r; }
      }
    }
    if (rep) {
      stats.replaced++;
      sentences.push({ text: body, kinds, action: 'replace', out: rep });
      out += rep + gap;
    } else {
      stats.hidden++;
      sentences.push({ text: body, kinds, action: 'hide', out: null });
      // 숨긴 문장 뒤 구분자는 줄바꿈만 남긴다(공백 겹침 방지)
      out += /\n/.test(gap) ? gap : '';
    }
  }
  return { text: out, sentences, stats, changed: out !== src };
}

/** FAQ 배열 판정 — 답변(a)을 판정, 다 숨겨지면 문항째 뺀다. 질문(q)은 locked 이면 숫자만. */
export function factCheckFaqs<T extends { q: string; a: string }>(
  items: readonly T[] | null | undefined, f: FactFields, opts: FactCheckOptions = {},
): { items: T[]; stats: FactCheckStats; dropped: number } {
  const stats = emptyStats();
  const out: T[] = [];
  let dropped = 0;
  for (const it of items ?? []) {
    if (!it || typeof it.a !== 'string') continue;
    const r = factCheckText(it.a, f, opts);
    for (const k of Object.keys(stats) as Array<keyof FactCheckStats>) stats[k] += r.stats[k];
    if (!r.text.trim()) { dropped++; continue; }
    out.push(r.changed ? { ...it, a: r.text.trim() } : it);
  }
  return { items: out, stats, dropped };
}

/**
 * apt_sites 행 → 대조 필드. 분양가는 `priceShown`(출처·price-guard 판정 통과)일 때만 싣는다.
 * total_units 는 회차 현장에서 회차 공급 수라 complexUnits 로 쓰지 않는다(complex_units 만 — 지시 4).
 */
export function factFieldsFromSite(site: any, opts: { priceShown?: boolean; builder?: string | null; moveInYm?: string | null } = {}): FactFields {
  const s = site ?? {};
  const priceOk = opts.priceShown ?? (s.price_source !== 'synthetic');
  return {
    complexUnits: num(s.complex_units),
    supplyUnits: num(s.supply_units),
    generalUnits: num(s.general_units),
    builder: opts.builder !== undefined ? opts.builder : (s.builder ?? null),
    priceMin: priceOk ? num(s.price_min) : null,
    priceMax: priceOk ? num(s.price_max) : null,
    moveInYm: opts.moveInYm ?? s.move_in_date ?? null,
    priceSource: s.price_source ?? null,
  };
}

/* ───────────────────────── 분양가 출처 정책(지시 3) ───────────────────────── */

/**
 * 분양가가 «공고·인허가» 로 연결돼 있는가. 아니면 분양가를 노출하지 않는다(ds2 price-guard 와 같은 자리 — 데이터 단계 마스크).
 *   연결 = price_source(합성 제외) · source_ids.subscription_id(청약홈 공고) · source_ids.unsold_id(미분양 원장 — 공고 파생)
 *        · stage_source 「permit:」 · expected_sale_source 「permit」(주택 인허가).
 *   실측 10/16: 가격 보유·미연결 = 입주 후 trade 2,359 · landmark 10 · 정비 1 (price_source NULL 2,544 중).
 */
export function isPriceSourced(site: any): boolean {
  const s = site ?? {};
  const ps = String(s.price_source ?? '').trim();
  if (ps && ps !== 'synthetic') return true;
  const ids = (s.source_ids && typeof s.source_ids === 'object' ? s.source_ids : {}) as Record<string, unknown>;
  if (ids.subscription_id != null && String(ids.subscription_id) !== '') return true;
  if (ids.unsold_id != null && String(ids.unsold_id) !== '') return true;
  if (/^permit:/.test(String(s.stage_source ?? ''))) return true;
  if (String(s.expected_sale_source ?? '') === 'permit') return true;
  return false;
}

/** 상세 조회 묶음 `{ site }` 의 분양가 열을 출처 정책으로 비운다(대상 아니면 같은 객체). */
export function maskUnsourcedPrice<T extends { site?: any }>(d: T): T {
  const s = d?.site;
  if (!s || !(Number(s.price_min) > 0 || Number(s.price_max) > 0) || isPriceSourced(s)) return d;
  return { ...d, site: { ...s, price_min: null, price_max: null } };
}
